import { formatShortDate, parseLocalDate } from "./dateUtils";
import { aggregateGoals, groupAverage, memberRate } from "./progressCompare";
import { avatarOf } from "./titleImage";

/** Göreli adresi (PUBLIC_URL altındaki rozet) PDF için mutlak adrese çevirir. */
export function absoluteUrl(path, origin) {
  if (!path) return null;
  if (/^(https?:|data:)/.test(path)) return path;
  const base = String(origin || "").replace(/\/$/, "");
  return `${base}${path.startsWith("/") ? "" : "/"}${path}`;
}

/**
 * Rapordaki üyenin rozet resmi: yalnız seçtiği profil resmi (avatar).
 * Seçmemiş üyede null; PDF'te eski görünüm kalır.
 */
export function memberBadge(u, origin) {
  const avatar = avatarOf(u);
  if (!avatar) return null;
  return { code: avatar.code, name: avatar.name || "", url: absoluteUrl(avatar.src, origin) };
}

/** Adın yanındaki ünvan: profil resmi ünvanı, yoksa en yüksek ünvan; yoksa "". */
export function memberTitleName(u) {
  return avatarOf(u)?.name || u?.title?.name || "";
}

/** Belgede kullanılacak rozet adresleri (tekrarsız). */
export function reportBadgeUrls(model) {
  return Array.from(new Set((model?.members || []).map((m) => m.badge?.url).filter(Boolean)));
}

const defaultOrigin = () => (typeof window !== "undefined" && window.location ? window.location.origin : "");

/**
 * Dosya adı grubun adından üretiliyor. Dosya sistemlerinde sorun çıkaran
 * karakterleri ayıklıyor, Türkçe harfleri koruyoruz.
 */
export function reportFileName(groupName) {
  const base = String(groupName || "")
    .replace(/[\\/:*?"<>|]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return `${base || "grup-raporu"}.pdf`;
}

export function formatRange(range) {
  const start = formatShortDate(parseLocalDate(range?.start));
  const end = formatShortDate(parseLocalDate(range?.end));
  return `${start} – ${end}`;
}

/**
 * Rapor gövdesi: özet, hedef kırılımı ve üye satırları. Hedefler ad + biçim
 * anahtarlı; üye oranı periodRate (yoksa completionRate), tam sayı.
 * activityName: biçimin görünen adı (aynı ad iki biçimde geçerse eklenir).
 */
export function buildReportModel({ group, userStats = [], range, activityName, origin = defaultOrigin() }) {
  const list = Array.isArray(userStats) ? userStats : [];
  const labels = aggregateGoals(list, activityName);

  const members = list
    .map((u) => ({
      id: String(u.actorId),
      name: `${u.name || ""} ${u.surname || ""}`.trim(),
      email: u.email || "",
      daysActiveCount: u.daysActiveCount || 0,
      perfectDaysCount: u.perfectDaysCount || 0,
      totalValue: u.totalValue || 0,
      totalGoalValue: u.totalGoalValue || 0,
      completionRate: memberRate(u),
      badge: memberBadge(u, origin),
      titleName: memberTitleName(u),
    }))
    .sort((a, b) => b.completionRate - a.completionRate);

  const activeMembers = list.filter((u) => (u.daysActiveCount || 0) > 0).length;

  return {
    groupName: group?.groupName || "",
    rangeLabel: formatRange(range),
    summary: {
      memberCount: list.length,
      activeMembers,
      totalActiveDays: list.reduce((sum, u) => sum + (u.daysActiveCount || 0), 0),
      avgCompletion: groupAverage(list.map(memberRate)),
    },
    labels,
    members,
  };
}
